/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AddPopUpComponent} from '../../../core/components/pop-ups/add-pop-up/add-pop-up.component';
import {DeletePopUpComponent} from '../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {ManageForecastStateService} from '../../../services/manage-forecast-state.service';


@Component({
    selector: 'app-manage-forecast-state',
    templateUrl: './manage-forecast-state.component.html',
    styleUrls: ['./manage-forecast-state.component.scss'],
    standalone: false
})
export class ManageForecastStateComponent implements OnInit {

  showSpinner: boolean;

  columnDef: ColDef[] = [
    {headerName: 'Forecast State ID', field: 'id'},
    {headerName: 'Forecast State', field: 'forecastState'},
  ];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  forecastStates: any;

  selectedValue = '';
  selectedId: number;

  constructor(private readonly service: ManageForecastStateService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {
  }


  ngOnInit(): void {
    this.getForecastState();
  }

  /**
   * Get all Forecast State.
   */
  getForecastState(): void {
    this.showSpinner = true;
    this.service.getForecastStates().subscribe(
      (data) => {
        this.forecastStates = data;
      },
      (error) => {
        this.errorHandle(error);
      },
      () => {
        this.showSpinner = false;
      });
  }

  /**
   * Update selected Forecast State with new value.
   */
  updateForecastState(): void {
    const dialogRef = this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update',
        currentValueToUpdate: this.selectedValue,
        hidePreview: true,
        maxLength: 50
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.showSpinner = true;
        this.service.updateForecastState(res.data.newValue, this.selectedId).subscribe({
          error: error => {
            this.errorHandle(error);
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully Updated.', DISMISS_MSG);
            this.getForecastState();
            this.selectedValue = '';
          }
        });
      }
    });
  }

  /**
   * Add a new Forecast State.
   */
  addForecastState(): void {
    const dialogRef = this.dialog.open(AddPopUpComponent, {
      data: {
        title: 'Add',
        maxLength: 50
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.showSpinner = true;
        this.service.addForecastState(res.data.newValue).subscribe({
          error: error => {
            this.errorHandle(error);
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully Added.', DISMISS_MSG);
            this.selectedValue = '';
            this.getForecastState();
          }
        });
      }
    });
  }

  /**
   * Delete selected Forecast State.
   */
  deleteForecastState(): void {
    const dialogRef = this.dialog.open(DeletePopUpComponent, {
      data: {
        title: 'Delete',
        valueToDelete: this.selectedValue
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res.data.deleteValue) {
        this.showSpinner = true;
        this.service.deleteForecastState(this.selectedId).subscribe({
          error: error => {
            this.errorHandle(error);
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully deleted Forecast State.', DISMISS_MSG);
            this.getForecastState();
            this.selectedValue = '';
          }
        });
      }
    });
  }


  /**
   * Event for selecting a forecast state
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedValue = (event.data.forecastState);
      this.selectedId = event.data.id;
    } else if (event.data.id === this.selectedId) {
      this.selectedValue = null;
      this.selectedId = null;
    }
  }


  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedValue = '';
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
